import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  MapPin,
  RefreshCw,
  Store,
} from "lucide-react";

import EmptyState from "@/components/common/EmptyState";
import GeneratedAvatar from "@/components/common/GeneratedAvatar";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listAdminShops } from "@/lib/api";
import { timeAgo } from "@/lib/formatters";
import { formatMoneyMinor } from "@/pages/overviewModel";

const PAGE_SIZE = 5;

function ShopsSkeleton() {
  return (
    <div className="card-elev overflow-hidden rounded-xl" role="status" aria-label="Loading shops">
      <span className="sr-only">Loading shops</span>
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className="flex h-[72px] items-center gap-4 border-b border-[#0E5B52]/8 px-4">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="w-56 space-y-2"><Skeleton className="h-4 w-36" /><Skeleton className="h-3 w-52" /></div>
          <Skeleton className="hidden h-4 w-28 md:block" />
          <Skeleton className="ml-auto h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export default function Shops() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [cursorStack, setCursorStack] = useState([""]);
  const [page, setPage] = useState(0);
  const debouncedQuery = query.trim();
  const cursor = cursorStack[page] || "";

  useEffect(() => {
    setCursorStack([""]);
    setPage(0);
  }, [debouncedQuery, plan, status]);

  const filters = useMemo(() => ({
    cursor,
    limit: PAGE_SIZE,
    plan,
    query: debouncedQuery,
    status,
  }), [cursor, debouncedQuery, plan, status]);

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery({
    queryKey: ["admin-shops", filters],
    queryFn: ({ signal }) => listAdminShops(filters, signal),
    placeholderData: keepPreviousData,
  });

  const items = data?.items || [];
  const total = Number(data?.total || 0);
  const firstItemNumber = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastItemNumber = Math.min(page * PAGE_SIZE + items.length, total);

  const nextPage = () => {
    if (!data?.nextCursor) return;
    setCursorStack((current) => {
      const next = current.slice(0, page + 1);
      next.push(data.nextCursor);
      return next;
    });
    setPage((current) => current + 1);
  };

  return (
    <div data-testid="shops-page">
      <PageHeader
        eyebrow="Customers"
        title="Shops"
        subtitle={`${total} ${total === 1 ? "shop" : "shops"} across all markets · Manage subscriptions, branches, staff and access.`}
      />

      <div className="card-elev mb-5 flex flex-col gap-3 rounded-xl p-4 md:flex-row">
        <PageSearchInput className="flex-1" testId="shops-search-input" value={query} onSearch={setQuery} placeholder="Search by shop, owner email or phone" inputClassName="h-9 rounded-md border-[#0E5B52]/10" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="shops-status-filter" className="h-9 w-full rounded-md border-[#0E5B52]/10 bg-white md:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="grace">Grace</SelectItem>
            <SelectItem value="requires_payment">Needs payment</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger data-testid="shops-plan-filter" className="h-9 w-full rounded-md border-[#0E5B52]/10 bg-white md:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {(data?.plans || []).map((option) => (
              <SelectItem key={option.id} value={option.code}>{option.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <ShopsSkeleton /> : isError && !data ? (
        <div className="card-elev rounded-xl p-12 text-center" role="alert">
          <div className="font-display text-lg font-semibold text-[#064E3B]">Unable to load shops</div>
          <p className="mt-1 text-sm text-[#6B7280]">{error?.message}</p>
          <Button onClick={() => refetch()} className="mt-4 bg-[#0E5B52] hover:bg-[#0A4640]">
            <RefreshCw className="mr-2 h-4 w-4" /> Try again
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="card-elev rounded-xl">
          <EmptyState
            title={total === 0 && !debouncedQuery && status === "all" && plan === "all" ? "No shops have registered yet" : "No shops match those filters"}
            description={total === 0 ? "Registered shops will appear here automatically." : "Try clearing the filters or broadening the search."}
            icon={Store}
          />
        </div>
      ) : (
        <div className={`card-elev overflow-hidden rounded-xl transition-opacity ${isFetching ? "opacity-70" : ""}`}>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F0F6F1]/60 hover:bg-[#F0F6F1]/60">
                {["Shop", "Location", "Plan", "Status", "Sync", "MRR", "Last login", "Action"].map((heading) => (
                  <TableHead key={heading} className={`text-[11px] font-semibold uppercase tracking-wider text-[#4B5563] ${heading === "Action" ? "text-right" : ""}`}>
                    {heading}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((shop) => (
                <TableRow
                  key={shop.id}
                  data-testid={`shop-row-${shop.id}`}
                  className="cursor-pointer hover:bg-[#F0F6F1]/50"
                  onClick={() => navigate(`/shops/${shop.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <GeneratedAvatar seed={shop.ownerId || shop.id} name={shop.ownerName} />
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-[#064E3B]">{shop.name}</div>
                        <div className="truncate text-xs text-[#4B5563]">
                          {shop.ownerName} · {shop.ownerPhone || shop.ownerEmail}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-[#4B5563]">
                      <MapPin className="h-3.5 w-3.5 text-[#0E5B52]/60" /> {shop.location}
                    </div>
                    <div className="text-xs text-[#9CA3AF]">{shop.branchCount} {shop.branchCount === 1 ? "branch" : "branches"}</div>
                  </TableCell>
                  <TableCell><span className="pill bg-[#F0F6F1] text-[#0E5B52]">{shop.planName}</span></TableCell>
                  <TableCell><StatusBadge status={shop.accessStatus} /></TableCell>
                  <TableCell><StatusBadge status={shop.syncStatus} /></TableCell>
                  <TableCell className="font-semibold text-[#064E3B]">
                    {shop.mrrMinor == null ? "Restricted" : formatMoneyMinor(shop.mrrMinor, shop.currencyCode)}
                  </TableCell>
                  <TableCell className="text-sm text-[#4B5563]">{shop.lastLoginAt ? timeAgo(shop.lastLoginAt) : "Never"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-[#0E5B52]">
                      Open <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {total > 0 && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[#0E5B52]/10 bg-white px-4 py-3 text-xs text-[#4B5563] sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing <strong className="font-semibold text-[#0F2A26]">{firstItemNumber}–{lastItemNumber}</strong> of {total} shops
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              aria-label="Go to previous shops page"
              disabled={page === 0 || isFetching}
              onClick={() => setPage((current) => current - 1)}
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Previous
            </Button>
            <span className="inline-flex h-8 min-w-16 items-center justify-center rounded-md bg-[#F0F6F1] px-3 font-semibold text-[#0E5B52]">
              Page {page + 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              aria-label="Go to next shops page"
              disabled={!data?.nextCursor || isFetching}
              onClick={nextPage}
            >
              Next <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
